import React, { useContext, useState } from "react";
import { GAME_STAGES } from "../constants/gameStages";
import { SocketContext } from "../context/SocketProvider";
import { useLanguage } from "../context/LanguageContext";
import { useFullscreen } from "../hooks/useFullscreen";
import ReturnHomeButton from "./ReturnHomeButton";
import SettingsModal from "./SettingsModal";
import logo from "../assets/logo.png";
import settingsIcon from "../assets/settings_icon.png";

// RoundDisplay component - Similar to TimerDisplay but only shows round info
const RoundDisplay = () => {
  const { round } = useContext(SocketContext);
  const { t } = useLanguage();
  const totalRounds = 4;

  return (
    <div className="w-48 h-24 relative">
      <div className="w-48 h-24 left-0 top-4 absolute bg-yellow300 rounded-bl-2xl rounded-br-2xl" />
      <div className="absolute left-1/2 top-[56px] -translate-x-1/2 text-center justify-center text-white text-2xl font-bold font-['Gabarito'] whitespace-nowrap">
        {`${t('score.round', 'ROUND')} ${round}/${totalRounds}`}
      </div>
    </div>
  );
};

const TaskBar = ({ imageCount, toggleImageCount, gameStarted, canSwapRoles }) => {
  const { gameStage, role, playMode, round } = useContext(SocketContext);
  const { t } = useLanguage();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { isFullscreen, isStandalone, requestFullscreen } = useFullscreen();

  const color =
    gameStage === GAME_STAGES.RESULTS
      ? "bg-yellow300"
      : role === "describer"
      ? "bg-pink300"
      : "bg-blue300";

  // Show round info on Results and Points pages (GAME_STAGES.RESULTS covers both)
  const showRoundInfo = gameStage === GAME_STAGES.RESULTS;

  return (
    <div className="relative z-10">
      {/* Top Taskbar */}
      <div
        className={`flex justify-between items-center px-6 py-4 text-white ${color}`}
      >
        {/* Game Logo */}
        <div className="flex-1 flex items-center gap-3">
          <ReturnHomeButton
            variant="logo"
            logoSrc={logo}
            logoAlt="Logo"
            logoClassName="w-[11.44rem] h-[1.94rem]"
          />
        </div>
        <div className="flex flex-1 justify-end items-center gap-4">
          {/* Fullscreen Button - Only show if not in fullscreen and not in standalone mode */}
          {!isFullscreen && !isStandalone && (
            <button
              onClick={requestFullscreen}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-300 focus:outline-none"
              aria-label="Enter fullscreen"
              title="Enter fullscreen (use Esc to exit)"
            >
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          
          {/* Settings Button */}
          <button
            onClick={() => setSettingsOpen(true)}
            className="pt-4 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all duration-300 focus:outline-none"
            aria-label="Open settings"
          >
            <img 
              src={settingsIcon} 
              alt="Settings" 
              className="w-6 h-6 filter brightness-0 invert"
            />
          </button>
        </div>
      </div>

      {/* Round Display - Shows on Results and Points pages */}
      {showRoundInfo && (
        <div className="fixed top-0 left-1/2 transform -translate-x-1/2" style={{ zIndex: 15 }}>
          <RoundDisplay />
        </div>
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        imageCount={imageCount}
        toggleImageCount={toggleImageCount}
        canSwapRoles={canSwapRoles}
      />
    </div>
  );
};

export default TaskBar;
